'use client'

import { useEffect, useRef } from 'react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

const CustomSwiper = ({
  slides = [],
  swiperOptions = {
    spaceBetween: 50,
    slidesPerView: 3,
    breakpoints: {},
    freeMode: {
      enabled: false,
    },
    loop: false,
    navigation: {
      enabled: true,
    },
    pagination: {
      clickable: true,
    },
    autoplay: {
      delay: 2500,
      disableOnInteraction: false,
    },
  },
}) => {
  const swiperInstanceRef = useRef(null)

  const mergedAutoplay = (() => {
    const userAutoplay = swiperOptions?.autoplay
    if (userAutoplay === false) return false
    const base = {
      delay: 2500,
      disableOnInteraction: false,
      pauseOnMouseEnter: false,
    }
    if (userAutoplay === true || userAutoplay == null) return base
    return { ...base, ...userAutoplay }
  })()

  const mergedOptions = { ...swiperOptions, autoplay: mergedAutoplay }

  const userOnSwiper = swiperOptions?.onSwiper
  const userOnInit = swiperOptions?.onInit
  const userOnTouchEnd = swiperOptions?.onTouchEnd

  const handleSwiper = (swiper) => {
    swiperInstanceRef.current = swiper
    try {
      if (swiper?.autoplay && swiper.autoplay.running === false) {
        swiper.autoplay.start()
      }
    } catch {}
    if (typeof userOnSwiper === 'function') userOnSwiper(swiper)
  }

  const handleInit = (swiper) => {
    try {
      if (swiper?.autoplay && swiper.autoplay.running === false) {
        swiper.autoplay.start()
      }
    } catch {}
    if (typeof userOnInit === 'function') userOnInit(swiper)
  }

  const handleTouchEnd = (swiper) => {
    try {
      if (swiper?.autoplay && swiper.params?.autoplay) {
        setTimeout(() => {
          try {
            if (
              !swiper.destroyed &&
              swiper.autoplay &&
              swiper.autoplay.running === false
            ) {
              swiper.autoplay.start()
            }
          } catch {}
        }, 300)
      }
    } catch {}
    if (typeof userOnTouchEnd === 'function') userOnTouchEnd(swiper)
  }

  useEffect(() => {
    const onVisibilityChange = () => {
      const swiper = swiperInstanceRef.current
      if (!swiper || !swiper.params?.autoplay) return
      try {
        if (document.visibilityState === 'hidden') {
          if (swiper.autoplay?.running) swiper.autoplay.stop()
        } else {
          if (swiper.autoplay && swiper.autoplay.running === false) {
            swiper.autoplay.start()
          }
        }
      } catch {}
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  const paginationOption = swiperOptions?.pagination
  const isPaginationEnabled =
    !!paginationOption &&
    (typeof paginationOption === 'boolean'
      ? paginationOption
      : paginationOption.enabled !== false)
  const combinedClassName = `${swiperOptions?.className ?? ''} ${
    isPaginationEnabled ? 'tw:!pb-8 tw:!overflow-y-auto' : ''
  }`.trim()
  return (
    <Swiper
      modules={[Navigation, Pagination, Autoplay]}
      {...mergedOptions}
      onSwiper={handleSwiper}
      onInit={handleInit}
      onTouchEnd={handleTouchEnd}
      className={combinedClassName}
    >
      {slides.map((child, idx) => (
        <SwiperSlide className={swiperOptions.slideClassName} key={idx}>
          {child}
        </SwiperSlide>
      ))}
    </Swiper>
  )
}

export default CustomSwiper
